import { supabase } from "./supabase/client";
import { mockService, MockResponse } from "./mockService";
import { Database } from "./supabase/schema";

// Detect if Supabase is configured
const isSupabaseConfigured = !!process.env.NEXT_PUBLIC_SUPABASE_URL;

// Offline sync queue for pending operations
const OFFLINE_QUEUE_KEY = "uniui_offline_queue";

function getOfflineQueue(): Array<{
  id: string;
  operation: string;
  data: unknown;
  timestamp: number;
}> {
  try {
    const stored = localStorage.getItem(OFFLINE_QUEUE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function setOfflineQueue(
  queue: Array<{
    id: string;
    operation: string;
    data: unknown;
    timestamp: number;
  }>,
) {
  try {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // Storage_full_or_unavailable
  }
}

export function addToOfflineQueue(operation: string, data: unknown): string {
  const queue = getOfflineQueue();
  const id = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  queue.push({ id, operation, data, timestamp: Date.now() });
  setOfflineQueue(queue);
  return id;
}

export async function flushOfflineQueue(): Promise<void> {
  const queue = getOfflineQueue();
  if (queue.length === 0) return;
  const remaining: Array<{
    id: string;
    operation: string;
    data: unknown;
    timestamp: number;
  }> = [];
  for (const item of queue) {
    try {
      if (item.operation === "updateProfile") {
        if (supabase) {
          await supabase
            .from("profiles")
            .update(item.data as unknown as never)
            .eq("id", (item.data as any).id);
        }
      }
      // Add_more_operation_types_as_needed
    } catch {
      remaining.push(item);
    }
  }
  setOfflineQueue(remaining);
}

export const db = {
  // --- Profile Operations ---
  updateProfile: async (id: string, data: unknown): Promise<MockResponse> => {
    if (isSupabaseConfigured && supabase) {
      const { error } = await supabase
        .from("profiles")
        .update(data as unknown as never)
        .eq("id", id);

      if (error) return { success: false, message: error.message };
      return { success: true, message: "Profile updated in Supabase" };
    }
    return mockService.update(id, data);
  },

  manageGroupMember: async (
    groupId: string,
    userId: string,
    action: "remove" | "promote",
  ): Promise<MockResponse> => {
    console.log(`Managing group ${groupId}: ${action} user ${userId}`);
    await new Promise((r) => setTimeout(r, 800));
    return {
      success: true,
      message: `User ${userId} successfully ${action}ed from group ${groupId}.`,
    };
  },

  getTickets: async (): Promise<unknown[]> => {
    return mockService.getTickets();
  },

  registerUser: async (userData: {
    user_id: string;
    institution: string;
    department_code: string;
    level: number;
    role: string;
    verification_document_path?: string;
  }): Promise<{ success: boolean; message: string }> => {
    if (isSupabaseConfigured && supabase) {
      // 1. Uniqueness check for course_rep
      if (userData.role === "course_rep") {
        const { count, error } = await supabase
          .from("profiles")
          .select("*", { count: "exact", head: true })
          .eq("department_code", userData.department_code)
          .eq("role", "course_rep");

        if (error) return { success: false, message: error.message };
        if (count && count > 0) {
          return {
            success: false,
            message:
              "A Course Representative already exists for this department.",
          };
        }
      }

      // 2. Perform_profile_update
      const updateData: Record<string, unknown> = {
        institution: userData.institution,
        department_code: userData.department_code,
        level: userData.level.toString(),
        role: userData.role,
      };

      if (
        userData.role === "course_rep" &&
        userData.verification_document_path
      ) {
        (updateData as any).kyc_status = "pending";
        (updateData as any).id_card_url = userData.verification_document_path;
      }

      const { error } = await supabase
        .from("profiles")
        .update(updateData as unknown as never)
        .eq("user_id", userData.user_id);

      if (error) return { success: false, message: error.message };
      return {
        success: true,
        message: "User registered successfully in Supabase",
      };
    }

    // Fallback to mockService
    return mockService.registerUser(userData);
  },

  // --- General Operations (wrapping mockService for other features) ---
  upload: async (file: File) => mockService.upload(file),
  save: async (data: unknown) => mockService.save(data),
  delete: async (id: string) => mockService.delete(id),
  update: async (id: string, updates: unknown) =>
    mockService.update(id, updates),
};
